import currencyData from './data/currency.json'
import currencyCodeData from './data/currency-code.json'

export interface ICurrency {
  symbol: string
  name: string
  symbol_native: string
  decimal_digits: number
  rounding: number
  code: string
  name_plural: string
  locale: string
}

export const currency = currencyData as Record<string, ICurrency>
export const currencyCode = currencyCodeData as Record<string, string>

const DEFAULT_CURRENCY_CODE = 'USD'

export { DEFAULT_CURRENCY_CODE }

const DEFAULT_NUMBER_LOCALE = 'en-US'
/** Fallback for `Intl` compact notation only; `en` keeps K/M-style output aligned with legacy affiliate compact display (vs `en-US` grouping quirks). */
const DEFAULT_COMPACT_LOCALE = 'en'

function normalizeCurrencyCode(currencyCode?: string): string {
  return currencyCode?.trim().toUpperCase() || DEFAULT_CURRENCY_CODE
}

export const getCurrencySymbol = (countryCode: string): string => {
  if (!countryCode) return ''
  return currency[countryCode]?.symbol || ''
}

export const getCurrencySymbolNative = (countryCode: string): string => {
  if (!countryCode) return ''
  return currency[countryCode]?.symbol_native || ''
}

export function getLocaleForCurrency(currencyCode: string): string {
  return currency[currencyCode]?.locale ?? DEFAULT_NUMBER_LOCALE
}

/**
 * Formats a numeric amount with currency symbol and number formatting (e.g. thousands separators).
 * Uses a fixed locale so server and client output match (SSR-safe).
 *
 * @param amount - Numeric amount or string to parse
 * @param currencyCode - Currency code (default USD)
 * @param decimalPlaces - Number of decimal places (default 2). Use 0 for no decimals (e.g. "US$ 123").
 * @param preferredSymbol - Portal settings symbol; used instead of code-derived symbol when set.
 */
export function formatCurrencyAmount(
  amount: number | string | undefined,
  currencyCode?: string,
  decimalPlaces: number = 2,
  preferredSymbol?: string
): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0)
  const code = normalizeCurrencyCode(currencyCode)
  const symbol = preferredSymbol?.trim() || getCurrencySymbol(code) || getCurrencySymbol(DEFAULT_CURRENCY_CODE)
  const locale = getLocaleForCurrency(code)
  const formattedNumber = new Intl.NumberFormat(locale, {
    numberingSystem: 'latn',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(numAmount)

  return `${symbol} ${formattedNumber}`
}

/**
 * Resolve locale with fallback for safe number formatting.
 */
export function resolveLocaleWithFallback(locale: string | undefined, fallbackLocale: string): string {
  const trimmed = locale?.trim()
  return trimmed || fallbackLocale
}

export function parseFiniteAmount(amount: unknown, fallback = 0): number {
  const parsed = typeof amount === 'string' ? Number(amount.trim()) : Number(amount ?? fallback)
  return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * Compact number (e.g. 1.2K, 3.4M).
 */
export function formatCompactDecimal(value: number, locale?: string): string {
  return new Intl.NumberFormat(resolveLocaleWithFallback(locale, DEFAULT_COMPACT_LOCALE), { notation: 'compact' }).format(value)
}

/**
 * Symbol + compact amount (e.g. `US$1.2M`); no space after symbol.
 */
export function formatCompactCurrencyAmount(
  amount: number | string | undefined,
  currencyCode?: string,
  locale?: string,
  preferredSymbol?: string
): string {
  const numAmount = parseFiniteAmount(amount)
  const code = normalizeCurrencyCode(currencyCode)
  const symbol = preferredSymbol?.trim() || getCurrencySymbol(code) || getCurrencySymbol(DEFAULT_CURRENCY_CODE)
  return `${symbol}${formatCompactDecimal(numAmount, locale)}`
}

export const StripeMinimumChargeAmount = {
  USD: 1,
  AED: 2,
  AUD: 1,
  BGN: 1,
  BRL: 1,
  CAD: 1,
  CHF: 1,
  CZK: 15,
  DKK: 2.5,
  EUR: 1,
  GBP: 1,
  HKD: 4,
  HRK: 5,
  HUF: 175,
  INR: 1,
  JPY: 50,
  MXN: 10,
  MYR: 2,
  NOK: 3,
  NZD: 1,
  PLN: 2,
  RON: 2,
  SEK: 3,
  SGD: 1,
  THB: 10,
  ILS: 3,
  PHP: 50,
  ZAR: 10,
  PKR: 150,
  NPR: 100,
  TWD: 20,
  VUV: 70,
  JOD: 0.5,
  IDR: 10000,
}

export const StripeZeroDecimalCurrencies = [
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]
